import { motion } from "framer-motion";
import {
  ArrowDown, ArrowUpRight, Users, PhoneCall, CalendarCheck, DollarSign,
  TrendingUp, UserCheck, Check, Camera, Home,
} from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { CountUp } from "./CountUp";
import { goToAudit } from "@/lib/scroll";

// ============================================================================
// SAMPLE CASE STUDIES — FICTIONAL DEMO DATA for design/layout purposes only.
// Every card is labeled "SAMPLE CASE STUDY" and an illustrative-data
// disclaimer renders at the bottom of this section. Replace with verified
// Viral Pulse client data when provided, then remove the labels + disclaimer.
// ============================================================================
const COMPACT_CASES = [
  {
    id: "hvac",
    category: "Local HVAC Company",
    platform: "Meta + Google Ads",
    stats: [
      { icon: Users, value: 184, label: "Qualified Leads" },
      { icon: DollarSign, value: 31, prefix: "$", label: "Avg Cost Per Lead" },
      { icon: CalendarCheck, value: 46, label: "Booked Appointments" },
      { icon: DollarSign, value: 5700, prefix: "$", label: "Ad Spend" },
    ],
    copy: "A local HVAC business needed a more consistent flow of homeowners looking for repair and replacement services. The campaign combined high-intent Google searches with Meta retargeting and lead generation.",
    highlight: "From searching → to calling → to booked appointment.",
  },
  {
    id: "law-firm",
    category: "Personal Injury Law Firm",
    platform: "Google Search Ads",
    stats: [
      { icon: Users, value: 93, label: "Qualified Inquiries" },
      { icon: PhoneCall, value: 71, label: "Phone Calls" },
      { icon: DollarSign, value: 84, prefix: "$", label: "Avg Cost Per Lead" },
      { icon: CalendarCheck, value: 30, suffix: " Days", label: "Campaign Period" },
    ],
    copy: "The campaign focused on high-intent local searches from potential clients actively looking for legal help — capturing demand at the exact moment someone searches for an attorney.",
    highlight: "High-intent traffic. Real phone calls. Real opportunities to sign clients.",
  },
  {
    id: "auto-detailing",
    category: "Auto Detailing Business",
    platform: "Meta Ads",
    stats: [
      { icon: Users, value: 148, label: "Qualified Leads" },
      { icon: DollarSign, value: 14.8, prefix: "$", decimals: 2, label: "Cost Per Lead" },
      { icon: CalendarCheck, value: 39, label: "Jobs Booked" },
      { icon: DollarSign, value: 18900, prefix: "$", label: "Est. Booked Revenue" },
    ],
    copy: "Instead of boosting posts, the campaign used direct-response creative, a compelling offer and a dedicated lead funnel.",
    highlight: "Stop paying for engagement. Start paying for opportunities.",
  },
  {
    id: "remodeling",
    category: "Home Remodeling Company",
    platform: "Meta + Google",
    stats: [
      { icon: Users, value: 117, label: "Qualified Project Leads" },
      { icon: DollarSign, value: 52, prefix: "$", label: "Cost Per Lead" },
      { icon: CalendarCheck, value: 28, label: "Estimates Scheduled" },
      { icon: DollarSign, value: 6100, prefix: "$", label: "Ad Spend" },
    ],
    copy: "Google captured homeowners already searching for remodeling services while Meta created additional demand and retargeted previous visitors.",
    highlight: "Google captures demand. Meta creates it. Viral Pulse connects both.",
  },
];

const FEATURED_CASES = [
  {
    id: "photographer",
    icon: Camera,
    category: "Photographer",
    platform: "Meta Lead Generation",
    headline: "From Inconsistent Bookings To A Steady Flow Of New Clients",
    description:
      "A local photographer wanted to stop relying almost entirely on referrals and create a more predictable way to bring in new customers. Viral Pulse built a targeted Meta advertising campaign focused on local customers who were actively interested in photography services.",
    didTitle: "What We Did",
    did: [
      "Meta lead generation campaigns",
      "Local audience targeting",
      "Strong photography-focused ad creative",
      "Lead forms built for easy inquiries",
      "Retargeting campaigns",
      "Ongoing audience and creative testing",
    ],
    stats: [
      { icon: Users, value: 94, label: "Leads Generated" },
      { icon: DollarSign, value: 13.84, prefix: "$", decimals: 2, label: "Cost Per Lead" },
      { icon: CalendarCheck, value: 27, label: "New Bookings" },
      { icon: DollarSign, value: 18.9, prefix: "$", suffix: "K", decimals: 1, label: "Booked Revenue" },
      { icon: TrendingUp, value: 4.6, suffix: "X", decimals: 1, label: "Return On Ad Spend" },
    ],
    closing: "Great photography gets attention. Great marketing turns that attention into bookings.",
  },
  {
    id: "real-estate",
    icon: Home,
    category: "Real Estate Agent",
    platform: "Meta + Google Ads",
    headline: "Turning Meta + Google Ads Into A Pipeline Of Buyer And Seller Leads",
    description:
      "A real estate agent wanted to grow beyond referrals, networking, and organic social media. Viral Pulse created a combined Meta and Google lead-generation strategy designed to reach both people actively searching for an agent and people considering buying or selling.",
    didGroups: [
      {
        title: "Meta campaigns focused on:",
        items: ["Home buyers", "First-time buyers", "Home sellers", "Home valuation offers", "Local listings", "Neighborhood targeting"],
      },
      {
        title: "Google campaigns focused on high-intent searches such as:",
        items: ["Homes for sale", "Real estate agents", "Realtors near me", "Selling a home", "Home values", "Local real estate searches"],
      },
    ],
    stats: [
      { icon: Users, value: 163, label: "Buyer + Seller Leads" },
      { icon: DollarSign, value: 21.37, prefix: "$", decimals: 2, label: "Cost Per Lead" },
      { icon: CalendarCheck, value: 31, label: "Qualified Appointments" },
      { icon: UserCheck, value: 8, label: "New Clients" },
      { icon: DollarSign, value: 74.5, prefix: "$", suffix: "K", decimals: 1, label: "Estimated Commission" },
      { icon: TrendingUp, value: 6.2, suffix: "X", decimals: 1, label: "Return On Ad Spend" },
    ],
    closing: "Your next client is already looking. We help make sure they find you.",
  },
];

// Featured funnel — sample HVAC demo data (fictional)
const FEATURED_FLOW = [
  { value: "$5,700", label: "Ad Spend" },
  { value: "Meta + Google", label: "Ads Running" },
  { value: "184", label: "Qualified Leads" },
  { value: "93", label: "Phone Conversations" },
  { value: "46", label: "Appointments" },
  { value: "New", label: "Customers" },
];

const SampleTag = () => (
  <span className="inline-flex items-center rounded-full border border-purple-500/40 bg-purple-600/15 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-purple-300">
    Case Study
  </span>
);

const StatBlock = ({ s, big }) => (
  <div>
    <p className={`font-display font-extrabold leading-none tracking-tighter text-white ${big ? "text-5xl sm:text-6xl" : "text-4xl sm:text-5xl"}`}>
      <CountUp value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} decimals={s.decimals || 0} />
    </p>
    <p className="mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
      <s.icon className="h-3 w-3 text-purple-400" />
      {s.label}
    </p>
  </div>
);

const FeaturedCase = ({ c, flip }) => (
  <Reveal>
    <div
      data-testid={`case-study-${c.id}`}
      className="overflow-hidden rounded-3xl border border-[#272732] bg-[#121217] transition-colors duration-300 hover:border-purple-500/40"
    >
      <div className={`grid grid-cols-1 lg:grid-cols-2 ${flip ? "lg:[direction:rtl]" : ""}`}>
        <div className="p-8 sm:p-12 lg:[direction:ltr]">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600/15 text-purple-400">
              <c.icon className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-sm font-extrabold uppercase tracking-[0.12em] text-white">
                {c.category}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-400">
                {c.platform}
              </p>
            </div>
            <span className="lg:ml-auto"><SampleTag /></span>
          </div>

          <h3 className="mt-7 font-display text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl">
            {c.headline}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.description}</p>

          <div className="mt-7">
            {c.did && (
              <>
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-purple-400">{c.didTitle}</p>
                <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {c.did.map((d) => (
                    <li key={d} className="flex items-start gap-2.5 text-sm font-medium text-zinc-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-purple-400" strokeWidth={3} />
                      {d}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {c.didGroups &&
              c.didGroups.map((g) => (
                <div key={g.title} className="mt-5 first:mt-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-purple-400">{g.title}</p>
                  <ul className="mt-3 grid grid-cols-2 gap-2.5">
                    {g.items.map((d) => (
                      <li key={d} className="flex items-start gap-2 text-[13px] font-medium text-zinc-300">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-400" strokeWidth={3} />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>

          <p className="mt-7 border-l-2 border-purple-500 pl-3 text-sm font-bold italic text-purple-300">
            {c.closing}
          </p>
          <button
            type="button"
            data-testid={`case-study-cta-${c.id}`}
            onClick={goToAudit}
            className="group mt-7 flex w-fit items-center gap-2 rounded-full bg-purple-600 px-7 py-3.5 text-xs font-extrabold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-purple-500"
          >
            Get My Free Marketing Audit
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className={`grid content-center grid-cols-2 gap-x-6 gap-y-9 border-t border-white/5 bg-white/[0.02] p-8 sm:p-12 lg:border-l lg:border-t-0 lg:[direction:ltr] ${c.stats.length % 2 ? "" : ""}`}>
          {c.stats.map((s) => (
            <StatBlock key={s.label} s={s} big />
          ))}
        </div>
      </div>
    </div>
  </Reveal>
);

export const Results = () => (
  <section id="results" className="relative border-y border-white/5 bg-[#07070C] py-12 sm:py-16">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Eyebrow>Performance Marketing</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl">
          Clicks Don't Impress Us.{" "}
          <span className="text-purple-500">Customers Do.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
          Our campaigns are built around the numbers that actually matter to a
          business.
        </p>
      </Reveal>

      {/* Featured funnel — sample data */}
      <Reveal delay={0.1}>
        <div
          data-testid="featured-case-study"
          className="mt-10 grid grid-cols-1 gap-10 overflow-hidden rounded-3xl border border-[#272732] bg-[#121217] p-8 sm:p-12 lg:grid-cols-2 lg:items-center"
        >
          <div>
            <SampleTag />
            <h3 className="mt-5 font-display text-3xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-4xl">
              This Is What We Mean By{" "}
              <span className="text-purple-500">Performance Marketing.</span>
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-zinc-400 md:text-base">
              The goal isn't traffic. The goal is turning advertising dollars
              into opportunities for your business.
            </p>
            <button
              type="button"
              data-testid="featured-results-cta"
              onClick={goToAudit}
              className="group mt-8 flex w-fit items-center gap-2 rounded-full bg-purple-600 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-purple-500"
            >
              I Want Results Like This
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <div className="relative">
            <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-500">
              How The Campaign Worked — Local HVAC Company
            </p>
            {FEATURED_FLOW.map((f, i) => (
              <div key={f.label}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className={`flex items-center justify-between rounded-xl border px-5 py-3.5 ${
                    i === FEATURED_FLOW.length - 1
                      ? "border-purple-500/60 bg-purple-600"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                  style={{ marginLeft: `${i * 4}%`, width: `${100 - i * 4}%` }}
                >
                  <span className="font-display text-lg font-extrabold tracking-tight text-white sm:text-xl">
                    {f.value}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${i === FEATURED_FLOW.length - 1 ? "text-purple-200" : "text-zinc-500"}`}>
                    {f.label}
                  </span>
                </motion.div>
                {i < FEATURED_FLOW.length - 1 && (
                  <div className="flex justify-center py-0.5" style={{ marginLeft: `${i * 4}%` }}>
                    <motion.span
                      animate={{ opacity: [0.25, 1, 0.25] }}
                      transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.2 }}
                    >
                      <ArrowDown className="h-4 w-4 text-purple-500" />
                    </motion.span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Two detailed case studies */}
      <Reveal delay={0.05}>
        <div className="mt-14">
          <Eyebrow>What Great Marketing Can Look Like</Eyebrow>
          <h3 className="mt-4 font-display text-3xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-4xl lg:text-5xl">
            More Leads. More Customers.{" "}
            <span className="text-purple-500">More Business.</span>
          </h3>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-400">
            See how the right Meta and Google advertising strategy can turn
            attention into real opportunities for your business.
          </p>
        </div>
      </Reveal>

      <div className="mt-10 space-y-6">
        {FEATURED_CASES.map((c, i) => (
          <FeaturedCase key={c.id} c={c} flip={i % 2 === 1} />
        ))}
      </div>

      {/* More sample results — compact cards */}
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {COMPACT_CASES.map((c, i) => (
          <Reveal key={c.id} delay={0.06 * i} className="h-full">
            <div
              data-testid={`case-study-${c.id}`}
              className="group flex h-full flex-col rounded-3xl border border-[#272732] bg-[#121217] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-purple-500/50"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-display text-sm font-extrabold uppercase tracking-[0.12em] text-white">
                  {c.category}
                </p>
                <SampleTag />
              </div>
              <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-purple-400">
                {c.platform}
              </p>

              <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-6">
                {c.stats.map((s) => (
                  <StatBlock key={s.label} s={s} />
                ))}
              </div>

              <p className="mt-6 flex-1 text-[13px] leading-relaxed text-zinc-400">{c.copy}</p>
              <p className="mt-4 border-l-2 border-purple-500 pl-3 text-[13px] font-bold italic text-purple-300">
                {c.highlight}
              </p>
              <button
                type="button"
                data-testid={`case-study-cta-${c.id}`}
                onClick={goToAudit}
                className="group/cta mt-6 flex w-fit items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.15em] text-white transition-colors hover:text-purple-400"
              >
                I Want Results Like This
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 text-center">
          <p className="font-display text-3xl font-extrabold tracking-tighter text-white sm:text-4xl">
            Want Results <span className="text-purple-500">Like These?</span>
          </p>
          <button
            type="button"
            data-testid="results-bottom-cta"
            onClick={goToAudit}
            className="group mx-auto mt-7 flex items-center gap-3 rounded-full bg-purple-600 px-10 py-5 font-display text-base font-extrabold uppercase tracking-wide text-white shadow-[0_10px_40px_-10px_rgba(139,92,246,0.5)] transition-all duration-300 hover:bg-purple-500"
          >
            Get My Free Marketing Audit
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <p
          data-testid="sample-data-disclaimer"
          className="mt-12 text-center text-xs font-medium text-zinc-500"
        >
          Illustrative campaign examples. Results vary based on industry, market,
          offer, advertising budget, and other factors. Verified Viral Pulse case
          studies will replace these examples before final publication.
        </p>
      </Reveal>
    </div>
  </section>
);
